import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  AttachMoney,
  Receipt,
  AccountBalance,
  ArrowForward,
  ShoppingBag,
} from '@mui/icons-material';
import invoiceService from '../../services/invoice.service';
import paymentService from '../../services/payment.service';
import PayrollService from '../../services/payroll.service';
import { AccountBalanceWallet } from '@mui/icons-material';

const FinanceOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    todayInvoices: 0,
    totalPayments: 0,
    todayPayments: 0,
    totalPayroll: 0,
    monthlyPayroll: 0,
    pendingPayroll: 0,
    recentInvoices: [] as any[],
    recentPayments: [] as any[],
    recentPayrolls: [] as any[],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];
        const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format

        const [
          invoicesResponse, 
          invoicesTodayResponse, 
          paymentsResponse, 
          paymentsTodayResponse, 
          recentInvoicesResponse, 
          recentPaymentsResponse,
          payrollResponse,
          monthlyPayrollResponse,
          pendingPayrollResponse,
          recentPayrollsResponse
        ] = await Promise.all([
          invoiceService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          invoiceService.getAll({ 'pagination[pageSize]': 1, 'filters[createdAt][$gte]': today }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          paymentService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          paymentService.getAll({ 'pagination[pageSize]': 1, 'filters[createdAt][$gte]': today }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          invoiceService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'createdAt:desc' }).catch(() => ({ data: { data: [] } })),
          paymentService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'createdAt:desc' }).catch(() => ({ data: { data: [] } })),
          PayrollService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          PayrollService.getAll({ 'filters[period][$eq]': currentMonth, 'pagination[pageSize]': 1000 }).catch(() => ({ data: { data: [] } })),
          PayrollService.getAll({ 'pagination[pageSize]': 1000 }).catch(() => ({ data: { data: [] } })),
          PayrollService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'period:desc', populate: ['employee'] }).catch(() => ({ data: { data: [] } })),
        ]);

        // Calculate total monthly payroll
        const monthlyPayrollData = monthlyPayrollResponse.data?.data || [];
        const monthlyPayrollTotal = monthlyPayrollData.reduce((sum: number, payroll: any) => {
          return sum + (parseFloat(payroll.attributes?.netSalary) || 0);
        }, 0);

        // Calculate pending payroll (draft + processed)
        const allPayrolls = pendingPayrollResponse.data?.data || [];
        const pendingCount = allPayrolls.filter((p: any) => {
          const status = p.attributes?.status;
          return status === 'draft' || status === 'processed';
        }).length;

        setStats({
          totalInvoices: invoicesResponse.data?.meta?.pagination?.total || 0,
          todayInvoices: invoicesTodayResponse.data?.meta?.pagination?.total || 0,
          totalPayments: paymentsResponse.data?.meta?.pagination?.total || 0,
          todayPayments: paymentsTodayResponse.data?.meta?.pagination?.total || 0,
          totalPayroll: payrollResponse.data?.meta?.pagination?.total || 0,
          monthlyPayroll: monthlyPayrollTotal,
          pendingPayroll: pendingCount,
          recentInvoices: recentInvoicesResponse.data?.data || [],
          recentPayments: recentPaymentsResponse.data?.data || [],
          recentPayrolls: recentPayrollsResponse.data?.data || [],
        });
      } catch (error) {
        console.error('Error fetching finance data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const StatCard = ({ title, value, icon, color, onClick }: any) => (
    <Card elevation={2} sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 6 } : {} }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>{value}</Typography>
        <Typography color="text.secondary" variant="body2">{title}</Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Finance Overview</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>{t('finance.overview')}</Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 3, mb: 4 }}>
          <StatCard title={t('finance.totalInvoices')} value={stats.totalInvoices} icon={<Receipt />} color="primary.main" onClick={() => navigate('/invoices')} />
          <StatCard title={t('finance.todayInvoices')} value={stats.todayInvoices} icon={<Receipt />} color="info.main" onClick={() => navigate('/invoices')} />
          <StatCard title={t('finance.totalPayments')} value={stats.totalPayments} icon={<AttachMoney />} color="success.main" onClick={() => navigate('/payments')} />
          <StatCard title={t('finance.todayPayments')} value={stats.todayPayments} icon={<AttachMoney />} color="secondary.main" onClick={() => navigate('/payments')} />
          <StatCard 
            title="Monthly Payroll" 
            value={`OMR ${stats.monthlyPayroll.toFixed(2)}`} 
            icon={<AccountBalanceWallet />} 
            color="warning.main" 
            onClick={() => navigate('/payroll')} 
          />
          <StatCard 
            title="Pending Payroll" 
            value={stats.pendingPayroll} 
            icon={<AccountBalanceWallet />} 
            color="error.main" 
            onClick={() => navigate('/payroll')} 
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
          <Card elevation={2}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">{t('finance.recentInvoices')}</Typography>
              <IconButton size="small" onClick={() => navigate('/invoices')}><ArrowForward /></IconButton>
            </Box>
            <CardContent>
              <List>
                {stats.recentInvoices.length > 0 ? (
                  stats.recentInvoices.map((invoice: any, index: number) => (
                    <React.Fragment key={invoice.id}>
                      <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/invoices/${invoice.id}`)}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1">{invoice.invoiceNumber || `INV-${invoice.id}`}</Typography>
                              <Chip label={invoice.status || 'pending'} size="small" color={invoice.status === 'paid' ? 'success' : 'warning'} />
                            </Box>
                          }
                          secondary={`Total: OMR ${invoice.total?.toFixed(2) || '0.00'}`}
                        />
                      </ListItem>
                      {index < stats.recentInvoices.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem><ListItemText primary="No recent invoices" /></ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">{t('finance.recentPayments')}</Typography>
              <IconButton size="small" onClick={() => navigate('/payments')}><ArrowForward /></IconButton>
            </Box>
            <CardContent>
              <List>
                {stats.recentPayments.length > 0 ? (
                  stats.recentPayments.map((payment: any, index: number) => (
                    <React.Fragment key={payment.id}>
                      <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/payments/${payment.id}`)}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1">Payment #{payment.id}</Typography>
                              <Typography variant="body2" color="success.main" fontWeight="bold">OMR {payment.attributes?.amount?.toFixed(2) || '0.00'}</Typography>
                            </Box>
                          }
                          secondary={payment.attributes?.paymentMethod || 'N/A'}
                        />
                      </ListItem>
                      {index < stats.recentPayments.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem><ListItemText primary="No recent payments" /></ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">Recent Payroll</Typography>
              <IconButton size="small" onClick={() => navigate('/payroll')}><ArrowForward /></IconButton>
            </Box>
            <CardContent>
              <List>
                {stats.recentPayrolls.length > 0 ? (
                  stats.recentPayrolls.map((payroll: any, index: number) => {
                    const attrs = payroll.attributes;
                    const employee = attrs?.employee?.data?.attributes;
                    const formatPeriod = (period: string) => {
                      if (!period) return '-';
                      try {
                        const [year, month] = period.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
                        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                      } catch {
                        return period;
                      }
                    };
                    return (
                      <React.Fragment key={payroll.id}>
                        <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/payroll/${payroll.id}/edit`)}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">
                                  {employee ? `${employee.firstName} ${employee.lastName}` : 'Employee'}
                                </Typography>
                                <Chip 
                                  label={attrs?.status || 'draft'} 
                                  size="small" 
                                  color={
                                    attrs?.status === 'paid' ? 'success' : 
                                    attrs?.status === 'processed' ? 'info' : 
                                    'warning'
                                  } 
                                />
                              </Box>
                            }
                            secondary={`${formatPeriod(attrs?.period)} - OMR ${(attrs?.netSalary || 0).toFixed(2)}`}
                          />
                        </ListItem>
                        {index < stats.recentPayrolls.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <ListItem><ListItemText primary="No recent payroll records" /></ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 3 }}>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/invoices')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Receipt sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">{t('finance.invoices')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/payments')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AttachMoney sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6">{t('finance.payments')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/expenses')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Receipt sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
              <Typography variant="h6">{t('navigation.expenses')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/payroll')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceWallet sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
              <Typography variant="h6">Payroll</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/accounts')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalance sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
              <Typography variant="h6">{t('finance.chartOfAccounts')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/purchase-orders')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShoppingBag sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h6">{t('finance.purchase')}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
  );
};

export default FinanceOverview;

